import React from "react";
import { useId } from "react";
import { useTranslation } from 'react-i18next';
import './feature-section-with-card-gradient.css';

export function FeaturesSectionWithCardGradient({
  title,
  services,
}: {
  title: string;
  services: Array<{ 
    id: number; 
    title: string; 
    titleKey?: string;
    description: string;
    descriptionKey?: string;
  }>;
}) {
  const { t } = useTranslation();
  
  return (
    <div className="section-features">
      {title && (
        <h2 className="centered-title-dark-bg tw-pb-[2em]">
          <h2>{title}</h2>
        </h2>
      )}
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-10 md:tw-gap-2 tw-max-w-7xl sm:tw-mx-[2em] md:tw-mx-[6em] lg:tw-mx-[12em] lg:tw-mt-[4em] features-grid">
        {services.map((service) => (
          <div
            key={service.id}
            className="tw-relative tw-bg-gradient-to-b tw-from-neutral-900 tw-to-neutral-950 tw-p-6 tw-rounded-3xl tw-overflow-hidden card-gradient"
          >
            <Grid size={20} />
            <p className="tw-text-base  features-title tw-relative tw-z-20">
              {service.titleKey ? t(service.titleKey, service.title) : service.title}
            </p>
            <p className="tw-text-[#dddd] tw-mt-4 tw-text-[17px] tw-leading-[1.47059] tw-font-normal tw-relative tw-z-20">
              {service.descriptionKey ? t(service.descriptionKey, service.description) : service.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

const grid = [
  {
    title: "HIPAA and SOC2 Compliant",
    description:
      "Our applications are HIPAA and SOC2 compliant, your data is safe with us, always.",
  },
  {
    title: "Automated Social Media Posting",
    description:
      "Schedule and automate your social media posts across multiple platforms to save time and maintain a consistent online presence.",
  },
  {
    title: "Advanced Analytics",
    description:
      "Gain insights into your social media performance with detailed analytics and reporting tools to measure engagement and ROI.",
  },
  {
    title: "Content Calendar",
    description:
      "Plan and organize your social media content with an intuitive calendar view, ensuring you never miss a post.",
  },
  {
    title: "Audience Targeting",
    description:
      "Reach the right audience with advanced targeting options, including demographics, interests, and behaviors.",
  },
  {
    title: "Social Listening",
    description:
      "Monitor social media conversations and trends to stay informed about what your audience is saying and respond in real-time.",
  },
  {
    title: "Customizable Templates",
    description:
      "Create stunning social media posts with our customizable templates, designed to fit your brand's unique style and voice.",
  },
  {
    title: "Collaboration Tools",
    description:
      "Work seamlessly with your team using our collaboration tools, allowing you to assign tasks, share drafts, and provide feedback in real-time.",
  },
];

export const Grid = ({
  pattern,
  size,
}: {
  pattern?: number[][];
  size?: number;
}) => {
  const p = pattern ?? [
    [Math.floor(Math.random() * 4) + 7, Math.floor(Math.random() * 6) + 1],
    [Math.floor(Math.random() * 4) + 7, Math.floor(Math.random() * 6) + 1],
    [Math.floor(Math.random() * 4) + 7, Math.floor(Math.random() * 6) + 1],
    [Math.floor(Math.random() * 4) + 7, Math.floor(Math.random() * 6) + 1],
    [Math.floor(Math.random() * 4) + 7, Math.floor(Math.random() * 6) + 1],
  ];
  return (
    <div className="tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-0 -tw-ml-20 -tw-mt-2 tw-h-full tw-w-full [mask-image:linear-gradient(white,transparent)]">
      <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-r [mask-image:radial-gradient(farthest-side_at_top,white,transparent)] tw-from-zinc-900/30 tw-to-zinc-900/30 tw-opacity-100">
        <GridPattern
          width={size ?? 20}
          height={size ?? 20}
          x="-12"
          y="4"
          squares={p}
          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-mix-blend-overlay tw-fill-white/10 tw-stroke-white/10"
        />
      </div>
    </div>
  );
};

export function GridPattern({ width, height, x, y, squares, ...props }: any) {
  const patternId = useId();

  return (
    <svg aria-hidden="true" {...props}>
      <defs>
        <pattern
          id={patternId}
          width={width}
          height={height}
          patternUnits="userSpaceOnUse"
          x={x}
          y={y}
        >
          <path d={`M.5 ${height}V.5H${width}`} fill="none" />
        </pattern>
      </defs>
      <rect
        width="100%"
        height="100%"
        strokeWidth={0}
        fill={`url(#${patternId})`}
      />
      {squares && (
        <svg x={x} y={y} className="tw-overflow-visible">
          {squares.map(([x, y]: any) => (
            <rect
              strokeWidth="0"
              key={`${x}-${y}`}
              width={width + 1}
              height={height + 1}
              x={x * width}
              y={y * height}
            />
          ))}
        </svg>
      )}
    </svg>
  );
}